import api from './api';
import { IProduct } from '@gff/shared';

// Stats Interface
export interface ProductStats {
  total: number;
  active: number;
  outOfStock: number;
  lowStock: number;
  categories: string[];
  recentlyUpdated: any[];
}

export const fetchProductStats = async (): Promise<ProductStats> => {
  try {
    // Call is just '/products/stats' -> api adds dynamic base
    const response = await api.get('/products/stats');
    return response.data;
  } catch (error) {
    console.error('Error fetching product stats:', error);
    // Return zeroed stats on error to prevent UI crash
    return { total: 0, active: 0, outOfStock: 0, lowStock: 0, categories: [], recentlyUpdated: [] };
  }
};

export const getProducts = async (params?: any) => {
  const response = await api.get('/products', { params });

  let products: any[] = [];
  let total = 0;
  let totalPages = 1;

  // Case 1: Pagination with "products" key (Current API behavior)
  if (response.data && Array.isArray(response.data.products)) {
    products = response.data.products;
    total = response.data.total || products.length;
    totalPages = response.data.totalPages || Math.ceil(total / (params?.limit || 12)) || 1;
  }
  // Case 2: Standard Pagination ({ data: [] })
  else if (response.data && Array.isArray(response.data.data)) {
    products = response.data.data;
    total = response.data.meta?.total || products.length;
    totalPages = response.data.meta?.totalPages || Math.ceil(total / (params?.limit || 12)) || 1;
  }
  // Case 3: Flat Array (Legacy)
  else if (Array.isArray(response.data)) {
    products = response.data;
    total = products.length;
    totalPages = 1;
  } else {
    console.warn('[ProductService] Unexpected response format', response.data);
  }

  return { products, total, totalPages };
};

export const getProduct = async (id: string): Promise<IProduct> => {
  const response = await api.get(`/products/${id}`);
  return response.data;
};

export const uploadImage = async (file: File): Promise<string> => {
  const formData = new FormData();
  formData.append('image', file);
  const response = await api.post('/products/upload', formData);
  return response.data.url;
};

export const createProduct = async (data: FormData | Partial<IProduct>) => {
  return api.post('/products', data);
};

export const updateProduct = async (id: string, data: FormData | Partial<IProduct>) => {
  return api.put(`/products/${id}`, data);
};

export interface MenuOcrPreviewVariant {
  tempId: string;
  name: string;
  price: number;
  priceMinor: number;
  stock: number;
}

export interface MenuOcrPreviewItem {
  tempId: string;
  name: string;
  sku: string;
  categoryName: string;
  description: string;
  price: number;
  priceMinor: number;
  currency: 'PKR' | 'AED';
  unit: string;
  stock: number;
  hasVariants: boolean;
  variants: MenuOcrPreviewVariant[];
  isVisiblePK: boolean;
  isVisibleUAE: boolean;
}

export const parseMenuAssetsOcr = async (
  files: File[],
  region: 'PK' | 'UAE',
  rawText?: string
): Promise<{
  status: string;
  region: 'PK' | 'UAE';
  currency: 'PKR' | 'AED';
  zeroCurrencyMultiplier: boolean;
  items: MenuOcrPreviewItem[];
}> => {
  const formData = new FormData();
  formData.append('region', region);
  if (rawText) {
    formData.append('rawText', rawText);
  }
  for (const file of files) {
    formData.append('files', file);
  }
  const response = await api.post('/products/menu-ocr/parse', formData);
  return response.data;
};

export const commitMenuAssetsOcr = async (
  region: 'PK' | 'UAE',
  items: MenuOcrPreviewItem[]
): Promise<{
  status: string;
  region: 'PK' | 'UAE';
  currency: 'PKR' | 'AED';
  categoriesUpserted: number;
  productsUpserted: number;
  productsModified: number;
  totalCommitted: number;
}> => {
  const response = await api.post('/products/menu-ocr/commit', {
    region,
    items,
  });
  return response.data;
};

export const updateRecipe = async (id: string, recipe: any[], yieldData?: { recipeYield: number; recipeUnitCount: number }) => {
  return api.patch(`/products/${id}/recipe`, {
    recipe,
    ...yieldData
  });
};

export const deleteProduct = async (id: string) => {
  return api.delete(`/products/${id}`);
};

export const fetchAllCategories = async (): Promise<{ _id: string; name: string }[]> => {
  try {
    const response = await api.get('/categories');
    return response.data;
  } catch (error) {
    return [];
  }
};

export const fetchAllTags = async (): Promise<string[]> => {
  return [];
};